import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';

/**
 * The single toast API every feature uses (docs/development/FrontendGuidelines.md §9.4) — never a
 * feature-local toast component. Toasts are for transient, non-blocking feedback only; a failed
 * mutation that blocks the user's task also updates the relevant UI's error state, not just this.
 */
export function useToast() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showSuccess = useCallback(
    (message: string) => enqueueSnackbar(message, { variant: 'success' }),
    [enqueueSnackbar],
  );
  const showError = useCallback(
    (message: string) => enqueueSnackbar(message, { variant: 'error' }),
    [enqueueSnackbar],
  );
  const showWarning = useCallback(
    (message: string) => enqueueSnackbar(message, { variant: 'warning' }),
    [enqueueSnackbar],
  );
  const showInfo = useCallback(
    (message: string) => enqueueSnackbar(message, { variant: 'info' }),
    [enqueueSnackbar],
  );

  return useMemo(
    () => ({ showSuccess, showError, showWarning, showInfo, dismiss: closeSnackbar }),
    [showSuccess, showError, showWarning, showInfo, closeSnackbar],
  );
}
